import { useState, useRef, useCallback } from 'react';
import { useInfiniteScrolling } from 'utils/hooks/useInfiniteScrolling';
import { useCloningCampaignsContext } from '../context';
import { CampaignsTemplates } from './CampaignsTemplates';
import { LoadingCampaignsSpinner } from './LoadingCampaignsSpinner';
import {
  CAMPAIGN_TABLE_COUNT_ID,
  CAMPAIGN_TABLE_HEADER_ID,
  CLONING_CAMPAIGNS_ID,
  NO_CLONEABLE_CAMPAIGNS_TEXT,
  CAMPAIGN_TABLE_ID,
} from './constants';
import {
  CampaignsTableContainer,
  Column,
  CampaignsColumnContainer,
  Container,
  Header,
  LoadingWrapper,
  NoCloneableCampaignsText,
} from './styles';
import { ExcludedCampaignsTemplates } from './ExcludedCampaignsTemplates';

const BASE_COUNT = 10;
export const CloningCampaigns = () => {
  const [index, setIndex] = useState(BASE_COUNT);
  const { areOptionsLoaded, excludedCampaigns, cloneableCampaigns, containerRef } =
    useCloningCampaignsContext();
  const campaignNodes = Object.values(cloneableCampaigns);
  const [campaigns, setCampaigns] = useState(campaignNodes?.slice(0, index));
  const count = campaignNodes?.length;

  const innerRef = useRef(null);

  const handleFetchMore = useCallback(() => {
    setIndex((prevIndex) => {
      const newIndex = prevIndex + BASE_COUNT;
      setCampaigns(campaignNodes?.slice(0, newIndex));
      return newIndex;
    });
  }, [campaignNodes, setCampaigns, setIndex]);

  useInfiniteScrolling({
    container: containerRef,
    trigger: innerRef,
    canFetch: index < count,
    onFetch: handleFetchMore,
  });

  const cloneableCampaignsCount = campaignNodes.length;
  const excludedCampaignsList = Object.values(excludedCampaigns);
  const showExcludedCampaigns = areOptionsLoaded && !!excludedCampaignsList?.length;

  return (
    <>
      <Container data-test={CLONING_CAMPAIGNS_ID}>
        {areOptionsLoaded ? (
          <>
            <Header
              data-test={CAMPAIGN_TABLE_COUNT_ID}
            >{`Campaigns to be copied (${cloneableCampaignsCount})`}</Header>
            {!!cloneableCampaignsCount && (
              <CampaignsColumnContainer data-test={CAMPAIGN_TABLE_HEADER_ID} $isCloneable>
                <Column>ID</Column>
                <Column>Campaign name</Column>
                <Column>Set message type</Column>
                <Column>Set sender</Column>
              </CampaignsColumnContainer>
            )}
          </>
        ) : (
          <LoadingCampaignsSpinner />
        )}
        <CampaignsTableContainer
          data-test={CAMPAIGN_TABLE_ID}
          $areOptionsLoaded={areOptionsLoaded}
          $isCloneable
        >
          {count > 0 && campaigns?.map((c) => <CampaignsTemplates key={c?.id} campaign={c} />)}
          {index < count && (
            <LoadingWrapper ref={innerRef} key={`loader-${index}`}>
              Loading more...
            </LoadingWrapper>
          )}
          {areOptionsLoaded && !cloneableCampaignsCount && (
            <NoCloneableCampaignsText data-test={NO_CLONEABLE_CAMPAIGNS_TEXT}>
              There are no campaigns available to copy
            </NoCloneableCampaignsText>
          )}
        </CampaignsTableContainer>
      </Container>
      {!!showExcludedCampaigns && (
        <ExcludedCampaignsTemplates excludedCampaigns={excludedCampaignsList} />
      )}
    </>
  );
};
